import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  profile_image?: string;
  is_subscribed: boolean;
  status: string;
  register_timestamp?: string;
  last_password_change: string;
  failed_login_attempts: number;
  created_at: string;
  updated_at: string;
  email_preferences: EmailPreference[];
  oapi_user_plan?: OapiUserPlan;
}

interface OapiUserPlan {
  id: number;
  user_id: number;
  plan_id: number;
  stripe_subscription_id?: number;
  status: number; // 0: FREE, 1: PAID, 2: SUSPENDED
  created_at: string;
  updated_at: string;
}

interface EmailPreference {
  id: number;
  email_type: string;
  receive_emails: boolean;
  unsubscribed_at?: string;
  created_at: string;
  updated_at: string;
}

interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  profile_image?: string;
}

interface UpdateUserRequest {
  name?: string;
  email?: string;
  profile_image?: string;
  status?: number;
}

interface UpdatePasswordRequest {
  current_password: string;
  new_password: string;
}

interface UpdateEmailPreferenceRequest {
  email_type: string;
  receive_emails: boolean;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Search and filter states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [emailPreferenceDialogOpen, setEmailPreferenceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    profile_image: '',
  });
  
  const [editForm, setEditForm] = useState<UpdateUserRequest>({});
  const [passwordForm, setPasswordForm] = useState<UpdatePasswordRequest>({
    current_password: '',
    new_password: '',
  });
  const [emailPreferenceForm, setEmailPreferenceForm] = useState<UpdateEmailPreferenceRequest>({
    email_type: 'promotional',
    receive_emails: true,
  });
  
  // Selected user states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  
  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // Load users on component mount and when filters change
  useEffect(() => {
    loadUsers();
  }, [page, search, statusFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/users/api?${params.toString()}`);
      const data: UserListResponse = response.data.data;
      
      setUsers(data.users);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error('Failed to load users:', error);
      showNotification('사용자 목록을 불러오는데 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      await api.post('/users/api', createForm);
      showNotification('사용자가 성공적으로 생성되었습니다', 'success');
      setCreateDialogOpen(false);
      resetCreateForm();
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.error || '사용자 생성에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUserId) return;
    
    try {
      await api.put(`/users/api/${editingUserId}`, editForm);
      showNotification('사용자 정보가 성공적으로 수정되었습니다', 'success');
      setEditDialogOpen(false);
      resetEditForm();
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.error || '사용자 정보 수정에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/api/${selectedUser.id}`);
      showNotification('사용자가 성공적으로 삭제되었습니다', 'success');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.error || '사용자 삭제에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const handleSoftDeleteUser = async (userId: number) => {
    try {
      await api.post(`/users/api/${userId}/soft-delete`);
      showNotification('사용자가 성공적으로 소프트 삭제되었습니다', 'success');
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.error || '사용자 소프트 삭제에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const handleUpdatePassword = async () => {
    if (!selectedUser) return;
    
    try {
      await api.put(`/users/api/${selectedUser.id}/password`, passwordForm);
      showNotification('비밀번호가 성공적으로 수정되었습니다', 'success');
      setPasswordDialogOpen(false);
      resetPasswordForm();
    } catch (error: any) {
      const message = error.response?.data?.error || '비밀번호 수정에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const handleResetPassword = async (userId: number) => {
    try {
      const response = await api.post(`/users/api/${userId}/reset-password`);
      const newPassword = response.data.data.new_password;
      showNotification(`새 비밀번호: ${newPassword}`, 'info');
    } catch (error: any) {
      const message = error.response?.data?.error || '비밀번호 초기화에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const handleUpdateEmailPreference = async () => {
    if (!selectedUser) return;
    
    try {
      await api.put(`/users/api/${selectedUser.id}/email-preference`, emailPreferenceForm);
      showNotification('이메일 설정이 성공적으로 수정되었습니다', 'success');
      setEmailPreferenceDialogOpen(false);
      resetEmailPreferenceForm();
      loadUsers();
    } catch (error: any) {
      const message = error.response?.data?.error || '이메일 설정 수정에 실패했습니다';
      showNotification(message, 'error');
    }
  };

  const openEditDialog = (user: User) => {
    setEditingUserId(user.id);
    setEditForm({
      name: user.name,
      email: user.email,
      profile_image: user.profile_image,
      status: getStatusValue(user.status),
    });
    setEditDialogOpen(true);
  };

  const openViewDialog = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const openPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setPasswordDialogOpen(true);
  };

  const openEmailPreferenceDialog = (user: User) => {
    setSelectedUser(user);
    const promotionalPref = user.email_preferences.find(p => p.email_type === 'PROMOTIONAL');
    setEmailPreferenceForm({
      email_type: 'PROMOTIONAL',
      receive_emails: promotionalPref ? promotionalPref.receive_emails : true,
    });
    setEmailPreferenceDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const getStatusValue = (statusText: string): number => {
    switch (statusText) {
      case '활성': return 0;
      case '비활성': return 1;
      case '차단': return 2;
      default: return 0;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '활성': return 'success';
      case '비활성': return 'warning';
      case '차단': return 'error';
      default: return 'default';
    }
  };

  const getEmailPreferenceStatus = (preferences: EmailPreference[], emailType: string): boolean => {
    const preference = preferences.find(p => p.email_type === emailType);
    return preference ? preference.receive_emails : true; // preference가 없으면 기본값 true
  };

  const getActiveEmailPreferences = (preferences: EmailPreference[]): EmailPreference[] => {
    const activePreferences: EmailPreference[] = [];
    
    // PROMOTIONAL 타입 확인
    const promotionalPref = preferences.find(p => p.email_type === 'PROMOTIONAL');
    if (promotionalPref ? promotionalPref.receive_emails : true) {
      activePreferences.push({
        id: promotionalPref?.id || 0,
        email_type: 'PROMOTIONAL',
        receive_emails: true,
        created_at: promotionalPref?.created_at || '',
        updated_at: promotionalPref?.updated_at || ''
      });
    }
    
    // SERVICE_NOTIFICATION 타입 확인
    const servicePref = preferences.find(p => p.email_type === 'SERVICE_NOTIFICATION');
    if (servicePref ? servicePref.receive_emails : true) {
      activePreferences.push({
        id: servicePref?.id || 0,
        email_type: 'SERVICE_NOTIFICATION',
        receive_emails: true,
        created_at: servicePref?.created_at || '',
        updated_at: servicePref?.updated_at || ''
      });
    }
    
    return activePreferences;
  };

  const formatEmailType = (emailType: string): string => {
    switch (emailType) {
      case 'PROMOTIONAL':
        return 'Promotional';
      case 'SERVICE_NOTIFICATION':
        return 'Service Notification';
      default:
        return emailType;
    }
  };

  const getSubscriptionStatus = (user: User): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
    if (!user.oapi_user_plan) {
      return { label: 'Not Subscribed', color: 'default' };
    }
    
    switch (user.oapi_user_plan.status) {
      case 0:
        return { label: 'Free', color: 'default' };
      case 1:
        return { label: 'Paid', color: 'success' };
      case 2:
        return { label: 'Suspended', color: 'error' };
      default:
        return { label: 'Unknown', color: 'default' };
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      name: '',
      email: '',
      password: '',
      profile_image: '',
    });
  };

  const resetEditForm = () => {
    setEditForm({});
    setEditingUserId(null);
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      current_password: '',
      new_password: '',
    });
  };

  const resetEmailPreferenceForm = () => {
    setEmailPreferenceForm({
      email_type: 'PROMOTIONAL',
      receive_emails: true,
    });
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSearch = () => {
    setPage(1);
    loadUsers();
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            label={`Total Users: ${total.toLocaleString()}`}
            color="primary"
            variant="outlined"
            size="medium"
          />
        </Box>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search (Name or Email)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <TextField
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="0">Active</MenuItem>
                  <MenuItem value="1">Inactive</MenuItem>
                  <MenuItem value="2">Blocked</MenuItem>
                </TextField>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleSearch}
                fullWidth
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
            <Grid item xs={12} md={1}>
              <Tooltip title="Refresh">
                <IconButton onClick={loadUsers} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* Users Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>OAPI Subscription</TableCell>
                <TableCell>Email Preferences</TableCell>
                <TableCell>Login Failures</TableCell>
                <TableCell>Registration Date</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.status}
                      color={getStatusColor(user.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getSubscriptionStatus(user).label}
                      color={getSubscriptionStatus(user).color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {getActiveEmailPreferences(user.email_preferences).map((pref) => (
                        <Chip
                          key={pref.email_type}
                          label={formatEmailType(pref.email_type)}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {getActiveEmailPreferences(user.email_preferences).length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          No active preferences
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.failed_login_attempts}</TableCell>
                  <TableCell>{user.created_at}</TableCell>
                  <TableCell>{user.updated_at}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => openViewDialog(user)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Soft Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleSoftDeleteUser(user.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Profile Image URL"
                value={createForm.profile_image}
                onChange={(e) => setCreateForm({ ...createForm, profile_image: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User Information</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Profile Image URL"
                value={editForm.profile_image || ''}
                onChange={(e) => setEditForm({ ...editForm, profile_image: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  select
                  label="Status"
                  value={editForm.status || 0}
                  onChange={(e) => setEditForm({ ...editForm, status: Number(e.target.value) })}
                >
                  <MenuItem value={0}>Active</MenuItem>
                  <MenuItem value={1}>Inactive</MenuItem>
                  <MenuItem value={2}>Blocked</MenuItem>
                </TextField>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">Basic Information</Typography>
                <Paper sx={{ p: 2, mt: 1 }}>
                  <Typography><strong>ID:</strong> {selectedUser.id}</Typography>
                  <Typography><strong>Name:</strong> {selectedUser.name}</Typography>
                  <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
                  <Typography><strong>Status:</strong> {selectedUser.status}</Typography>
                  <Typography><strong>OAPI Subscription:</strong> {getSubscriptionStatus(selectedUser).label}</Typography>
                  <Typography><strong>Login Failures:</strong> {selectedUser.failed_login_attempts}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6">System Information</Typography>
                <Paper sx={{ p: 2, mt: 1 }}>
                  <Typography><strong>Registration Date:</strong> {selectedUser.created_at}</Typography>
                  <Typography><strong>Last Updated:</strong> {selectedUser.updated_at}</Typography>
                  <Typography><strong>Password Changed:</strong> {selectedUser.last_password_change}</Typography>
                  {selectedUser.register_timestamp && (
                    <Typography><strong>Register Timestamp:</strong> {selectedUser.register_timestamp}</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Email Preferences</Typography>
                <Paper sx={{ p: 2, mt: 1 }}>
                  {getActiveEmailPreferences(selectedUser.email_preferences).length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getActiveEmailPreferences(selectedUser.email_preferences).map((pref) => (
                        <Chip
                          key={pref.email_type}
                          label={formatEmailType(pref.email_type)}
                          color="success"
                          size="small"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      No active email preferences
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Current Password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdatePassword} variant="contained">Change</Button>
        </DialogActions>
      </Dialog>

      {/* Email Preference Dialog */}
      <Dialog open={emailPreferenceDialogOpen} onClose={() => setEmailPreferenceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Email Receipt Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  select
                  label="Email Type"
                  value={emailPreferenceForm.email_type}
                  onChange={(e) => setEmailPreferenceForm({ ...emailPreferenceForm, email_type: e.target.value })}
                >
                  <MenuItem value="PROMOTIONAL">Promotional</MenuItem>
                  <MenuItem value="SERVICE_NOTIFICATION">Service Notification</MenuItem>
                </TextField>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={emailPreferenceForm.receive_emails}
                    onChange={(e) => setEmailPreferenceForm({ ...emailPreferenceForm, receive_emails: e.target.checked })}
                  />
                }
                label="Receive Emails"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailPreferenceDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateEmailPreference} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm User Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
